import { z } from 'zod';

// ─────────── Fase 8 · Rede entre revendas (docs/11) ───────────

export const TIPOS_COMISSAO_REDE = ['percentual', 'fixo'] as const;
export type TipoComissaoRede = (typeof TIPOS_COMISSAO_REDE)[number];

export const STATUS_ANUNCIO_REDE = ['ativo', 'pausado', 'vendido', 'retirado'] as const;
export type StatusAnuncioRede = (typeof STATUS_ANUNCIO_REDE)[number];
export const NOME_STATUS_ANUNCIO_REDE: Record<StatusAnuncioRede, string> = {
  ativo: 'Na rede',
  pausado: 'Pausado',
  vendido: 'Vendido pela rede',
  retirado: 'Retirado',
};

export const STATUS_OPORTUNIDADE_REDE = [
  'aberta',
  'venda_informada',
  'concluida',
  'cancelada',
] as const;
export type StatusOportunidadeRede = (typeof STATUS_OPORTUNIDADE_REDE)[number];
export const NOME_STATUS_OPORTUNIDADE: Record<StatusOportunidadeRede, string> = {
  aberta: 'Em negociação',
  venda_informada: 'Venda informada (aguardando a dona)',
  concluida: 'Repasse concluído',
  cancelada: 'Cancelada',
};

/** Campos que a loja dona escolhe mostrar depois do aceite (placa, chassi e clientes nunca). */
export const CAMPOS_REDE = [
  'versao',
  'km',
  'cor',
  'opcionais',
  'fotos',
  'fipe',
  'precoPublico',
] as const;
export type CampoRede = (typeof CAMPOS_REDE)[number];
export const NOME_CAMPO_REDE: Record<CampoRede, string> = {
  versao: 'Versão',
  km: 'Quilometragem',
  cor: 'Cor',
  opcionais: 'Opcionais',
  fotos: 'Fotos',
  fipe: 'FIPE',
  precoPublico: 'Preço de venda ao público',
};

export const entrarRedeSchema = z.object({
  nomeExibicao: z.string().trim().min(2).max(80),
  aceiteTermos: z.literal(true, { error: 'Aceite os termos da rede' }),
});

export const anuncioRedeSchema = z
  .object({
    precoRedeCentavos: z.number().int().positive('Informe o preço para as lojas'),
    comissaoTipo: z.enum(TIPOS_COMISSAO_REDE),
    /** percentual: centésimos de % (0 a 3000 = 30%) · fixo: centavos */
    comissaoValor: z.number().int().nonnegative(),
    mostrar: z
      .object({
        versao: z.boolean().default(true),
        km: z.boolean().default(true),
        cor: z.boolean().default(true),
        opcionais: z.boolean().default(true),
        fotos: z.boolean().default(true),
        fipe: z.boolean().default(true),
        precoPublico: z.boolean().default(false),
      })
      .prefault({}),
    /** rede: todas as lojas · parceiras: só as lojas de confiança da minha lista */
    visibilidade: z.enum(['rede', 'parceiras']).default('rede'),
    consentimento: z.literal(true, {
      error: 'Confirme que autoriza mostrar este carro na rede',
    }),
  })
  .refine((d) => d.comissaoTipo === 'fixo' || d.comissaoValor <= 3000, {
    message: 'Comissão de no máximo 30%',
    path: ['comissaoValor'],
  });
export type AnuncioRedeInput = z.infer<typeof anuncioRedeSchema>;

export const aceiteRedeSchema = z.object({
  aceite: z.literal(true, { error: 'Aceite a comissão para ver o carro' }),
});
export const vendaRedeSchema = z.object({
  valorVendaCentavos: z.number().int().positive('Informe o valor da venda ao cliente'),
});
export const cancelarRedeSchema = z.object({ motivo: z.string().trim().min(3).max(300) });
export const avaliacaoRedeSchema = z.object({
  nota: z.number().int().min(1).max(5),
  comentario: z.string().trim().max(500).optional(),
});
export const denunciaRedeSchema = z.object({
  motivo: z.string().trim().min(10, 'Descreva o que aconteceu').max(1000),
});

/** Horas que o carro fica reservado para a loja que aceitou (ninguém mais aceita nesse tempo). */
export const RESERVA_REDE_HORAS = 48;
/** Depois de informar a venda: reserva até a conclusão e prazo para mandar o comprovante. */
export const RESERVA_VENDA_HORAS = 7 * 24;
export const PRAZO_REPASSE_DIAS = 3;
/** Loja sem selo de verificada: até tantas negociações abertas ao mesmo tempo. */
export const LIMITE_SEM_SELO = 2;

export const filtroVitrineRedeSchema = z.object({
  busca: z.string().trim().max(60).optional(),
  uf: z.string().length(2).toUpperCase().optional(),
  precoMax: z.coerce.number().int().positive().optional(),
  precoMin: z.coerce.number().int().nonnegative().optional(),
  anoMin: z.coerce.number().int().min(1950).max(2100).optional(),
  anoMax: z.coerce.number().int().min(1950).max(2100).optional(),
  soVerificadas: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
  ordem: z.enum(['recentes', 'menor_preco', 'mais_perto']).default('recentes'),
});
export type FiltroVitrineRede = z.infer<typeof filtroVitrineRedeSchema>;

export const pedidoRedeSchema = z
  .object({
    marca: z.string().trim().min(2).max(40),
    modelo: z.string().trim().max(60).optional(),
    anoMin: z.number().int().min(1950).max(2100).optional(),
    anoMax: z.number().int().min(1950).max(2100).optional(),
    precoMaxCentavos: z.number().int().positive().optional(),
    uf: z.string().length(2).toUpperCase().optional(),
    observacao: z.string().trim().max(300).optional(),
  })
  .refine((d) => !d.anoMin || !d.anoMax || d.anoMin <= d.anoMax, {
    message: 'Ano inicial maior que o final',
    path: ['anoMax'],
  });
export type PedidoRedeInput = z.infer<typeof pedidoRedeSchema>;
/** Pedido fica no ar por 30 dias. */
export const DIAS_PEDIDO_REDE = 30;

export const mensagemRedeSchema = z.object({ texto: z.string().trim().min(1).max(1000) });
export const parceirasRedeSchema = z.object({
  tenantIds: z.array(z.string().uuid()).max(200),
});

/** O carro combina com o pedido? (marca/modelo por trecho, ano e preço máximos) */
export function combinaPedido(
  p: {
    marca: string;
    modelo: string | null;
    anoMin: number | null;
    anoMax: number | null;
    precoMaxCentavos: number | null;
    uf: string | null;
  },
  c: { marca: string; modelo: string; anoModelo: number; precoCentavos: number; uf: string | null },
): boolean {
  const tem = (a: string, b: string) => a.toLowerCase().includes(b.toLowerCase().trim());
  return (
    tem(c.marca, p.marca) &&
    (!p.modelo || tem(c.modelo, p.modelo)) &&
    (!p.anoMin || c.anoModelo >= p.anoMin) &&
    (!p.anoMax || c.anoModelo <= p.anoMax) &&
    (!p.precoMaxCentavos || c.precoCentavos <= p.precoMaxCentavos) &&
    (!p.uf || !c.uf || c.uf === p.uf)
  );
}

/** Simulador: se a loja vender por X, quanto vai para a dona e quanto sobra para ela. */
export function simularRede(
  t: { precoRedeCentavos: number; comissaoTipo: TipoComissaoRede; comissaoValor: number },
  valorVendaCentavos: number,
) {
  const comissao = comissaoRede(t.comissaoTipo, t.comissaoValor, valorVendaCentavos);
  return {
    repasseCentavos: t.precoRedeCentavos,
    comissaoCentavos: comissao,
    sobraCentavos: valorVendaCentavos - t.precoRedeCentavos - comissao,
  };
}

/** Comissão da loja dona sobre a venda (percentual sobre o valor de venda ao cliente final). */
export function comissaoRede(tipo: TipoComissaoRede, valor: number, valorVendaCentavos: number) {
  return tipo === 'fixo' ? valor : Math.round((valorVendaCentavos * valor) / 10_000);
}

export const textoComissao = (tipo: TipoComissaoRede, valor: number) =>
  tipo === 'fixo'
    ? (valor / 100).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        maximumFractionDigits: 0,
      })
    : `${(valor / 100).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}% do valor de venda`;

/** Termos que a loja interessada aceita (o texto exato vai para o hash do aceite). */
export function termosRede(t: {
  anuncioId: string;
  loja: string;
  carro: string;
  precoRedeCentavos: number;
  comissaoTipo: TipoComissaoRede;
  comissaoValor: number;
}) {
  const preco = (t.precoRedeCentavos / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
  return [
    `Anúncio ${t.anuncioId} · ${t.carro} · loja ${t.loja}.`,
    `Se a minha loja vender este carro, pagará à loja dona ${preco} pelo carro (repasse) e a comissão de ${textoComissao(t.comissaoTipo, t.comissaoValor)}.`,
    'Não vou contatar diretamente clientes da loja dona nem usar os dados do carro fora desta negociação.',
    'Todo acesso e esta aceitação ficam registrados com data e hora.',
  ].join(' ');
}

export const TERMOS_PARTICIPACAO_REDE =
  'Ao entrar na rede, minha loja aceita: cumprir as comissões aceitas, não abordar clientes de outras lojas, ' +
  'manter os dados dos carros em sigilo e ter cada acesso registrado. Quem descumprir pode ser bloqueado.';

// ── tipos devolvidos pela API ──

export interface ParticipanteRede {
  tenantId: string;
  nome: string;
  cidade: string | null;
  uf: string | null;
  notaMedia: number | null;
  avaliacoes: number;
  bloqueado: boolean;
  /** selo: assinatura paga, conta com 30+ dias e CNPJ cadastrado */
  verificada: boolean;
}

export interface AnuncioRedeVitrine {
  id: string;
  marca: string;
  modelo: string;
  anoModelo: number;
  cidade: string | null;
  uf: string | null;
  precoRedeCentavos: number;
  comissaoTipo: TipoComissaoRede;
  comissaoValor: number;
  publicadoEm: string;
  loja: ParticipanteRede;
  /** Oportunidade aberta da minha loja neste anúncio */
  minhaOportunidadeId: string | null;
  /** reservado para uma loja até esta data (null = livre) */
  reservadoAte: string | null;
  reservadoParaMim: boolean;
  soParceiras: boolean;
}

export interface CarroRede {
  marca: string;
  modelo: string;
  anoFabricacao: number;
  anoModelo: number;
  status: string;
  versao: string | null;
  km: number | null;
  cor: string | null;
  combustivel: string | null;
  cambio: string | null;
  opcionais: string[];
  fipeCentavos: number | null;
  precoPublicoCentavos: number | null;
  fotos: { m: string; g: string }[];
  loja: {
    nome: string;
    cidade: string | null;
    uf: string | null;
    whatsapp: string | null;
    telefone: string | null;
  };
}

export interface OportunidadeRede {
  id: string;
  papel: 'dona' | 'vendedora';
  status: StatusOportunidadeRede;
  anuncioId: string;
  carro: string;
  outraLoja: ParticipanteRede;
  precoRedeCentavos: number;
  comissaoTipo: TipoComissaoRede;
  comissaoValor: number;
  termos: string;
  termosHash: string;
  aceitoEm: string;
  valorVendaCentavos: number | null;
  comissaoCentavos: number | null;
  repasseCentavos: number | null;
  vendaInformadaEm: string | null;
  concluidaEm: string | null;
  canceladaEm: string | null;
  motivoCancelamento: string | null;
  jaAvaliei: boolean;
  /** até quando o carro está reservado para esta negociação */
  reservadoAte: string | null;
  prazoRepasse: string | null;
  comprovanteEm: string | null;
  /** venda informada, prazo vencido e sem comprovante */
  repasseAtrasado: boolean;
  mensagens: number;
}

export interface MensagemRede {
  id: string;
  minhaLoja: boolean;
  texto: string;
  criadoEm: string;
}

export interface PedidoRede {
  id: string;
  meu: boolean;
  loja: ParticipanteRede;
  marca: string;
  modelo: string | null;
  anoMin: number | null;
  anoMax: number | null;
  precoMaxCentavos: number | null;
  uf: string | null;
  observacao: string | null;
  expiraEm: string;
  criadoEm: string;
  /** meu pedido: anúncios da rede que combinam · pedido de outra loja: meus carros que combinam */
  combinam: { id: string; carro: string; precoCentavos: number; anuncioId: string | null }[];
}

export interface MeuAnuncioRede {
  id: string;
  veiculoId: string;
  carro: string;
  status: StatusAnuncioRede;
  precoRedeCentavos: number;
  comissaoTipo: TipoComissaoRede;
  comissaoValor: number;
  mostrar: Record<CampoRede, boolean>;
  consentimentoEm: string;
  oportunidades: number;
  visibilidade: 'rede' | 'parceiras';
  reservadoAte: string | null;
}

export interface EventoRede {
  seq: string;
  tipo: string;
  criadoEm: string;
  minhaLoja: boolean;
  dados: Record<string, unknown>;
  hash: string;
  hashAnterior: string | null;
}

export interface IndicadoresRede {
  participando: boolean;
  carrosNaRede: number;
  oportunidadesAbertas: number;
  vendidosPelaRedeMes: number;
  comprasPelaRedeMes: number;
  comissaoRecebidaMesCentavos: number;
  notaMedia: number | null;
  avaliacoes: number;
}

export const NOME_EVENTO_REDE: Record<string, string> = {
  entrou_na_rede: 'Entrou na rede',
  anuncio_publicado: 'Carro colocado na rede',
  anuncio_alterado: 'Anúncio alterado',
  anuncio_pausado: 'Anúncio pausado',
  anuncio_retirado: 'Anúncio retirado',
  anuncio_reativado: 'Anúncio reativado',
  comissao_aceita: 'Comissão aceita',
  carro_visualizado: 'Dados do carro vistos',
  venda_informada: 'Venda informada',
  repasse_confirmado: 'Repasse confirmado',
  oportunidade_cancelada: 'Oportunidade cancelada',
  avaliacao: 'Avaliação',
  carro_reservado: 'Carro reservado',
  comprovante_enviado: 'Comprovante do repasse enviado',
  pedido_publicado: 'Pedido publicado',
  parceiras_alteradas: 'Lojas parceiras alteradas',
  denuncia: 'Denúncia',
};
